import ExpandingBlock from '@/components/ExpandingBlock';
import Checkbox from '@/components/forms/Checkbox';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { marginX, paddingX } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

const StyledCheckbox = styled(Checkbox)`
  margin-right: ${remCalc(8)};
  flex-shrink: 0;
`;

const CategoryToggle = styled.button`
  ${customTextSize(19, 22)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  width: calc(100% + ${remCalc(16 * 2)});
  display: block;
  text-align: left;
  ${paddingX(16)}
  ${marginX(-16)}
  border-radius: ${remCalc(10)};
  height: ${remCalc(48)};
  background-color: transparent;

  display: inline-flex;
  align-items: center;
  justify-content: space-between;

  transition: background-color 0.1s ease-in-out;

  &:hover,
  &:active,
  &:focus {
    outline: none;
  }

  &:hover {
    background-color: ${hexToRGBA(COLORS.BLACK, 0.08)};
  }
`;

const FilterList = styled.ul`
  padding-top: ${remCalc(8)};
  padding-bottom: ${remCalc(38)};
`;

const FilterListItem = styled.li``;

interface FilterToggleProps {
  selected: boolean;
}

const FilterToggle = styled.button<FilterToggleProps>`
  display: block;
  text-align: left;
  padding: ${remCalc(9, 8, 8, 8)};
  ${marginX(-8)}
  min-height: ${remCalc(32)};
  border-radius: ${remCalc(10)};
  width: calc(100% + ${remCalc(8 * 2)});

  transition: background-color 0.1s ease-in-out;

  display: flex;
  align-items: flex-start;

  ${customTextSize(14, 16)};
  font-weight: ${(props) => (props.selected ? FONT_WEIGHTS.BOLD : FONT_WEIGHTS.NORMAL)};

  ${StyledCheckbox} {
    margin-top: -1px;
  }

  &:hover,
  &:active,
  &:focus {
    outline: none;
  }

  &:hover {
    background-color: ${hexToRGBA(COLORS.BLACK, 0.08)};
  }
`;

export type FilterBlock_Category = {
  title: string;
  slug: string;
  filters: FilterBlock_Filter[];
};

export type FilterBlock_Filter = {
  id: string;
  title: string;
  slug: string;
};

interface FilterBlockProps extends PropsWithClassName {
  collapsible: boolean;
  categories: FilterBlock_Category[];
  selections: string[];
  onFilterToggle: (category: FilterBlock_Category, filter: FilterBlock_Filter, selected: boolean) => void;
}

const FilterBlock: FC<FilterBlockProps> = (props) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  useEffect(() => {
    props.categories.forEach((category) => {
      if (
        category.filters.some((filter) => props.selections?.includes(filter.id)) &&
        !expandedCategories.includes(category.slug)
      ) {
        setExpandedCategories([...expandedCategories, category.slug]);
      }
    });
  }, [props.selections]);

  return (
    <ExpandingBlock className={props.className} title="Filters" forceOpen={!props.collapsible}>
      {props.categories?.map((category) => {
        const categoryOpen = expandedCategories.includes(category.slug);

        return (
          <div key={category.slug}>
            <CategoryToggle
              aria-expanded={categoryOpen}
              onClick={() => {
                if (categoryOpen) {
                  setExpandedCategories(expandedCategories.filter((slug) => slug !== category.slug));
                } else {
                  setExpandedCategories([...expandedCategories, category.slug]);
                }
              }}
            >
              <span>{category.title}</span>
              <span>{categoryOpen ? '-' : '+'}</span>
            </CategoryToggle>

            {categoryOpen && (
              <FilterList>
                {category.filters.map((filter) => {
                  const checked = props.selections?.includes(filter.id);

                  const onToggle = (checked: boolean) => {
                    props.onFilterToggle(category, filter, checked);
                  };

                  return (
                    <FilterListItem key={filter.slug}>
                      <FilterToggle selected={checked} onClick={() => onToggle(!checked)}>
                        <StyledCheckbox checked={checked} onToggle={onToggle} />
                        {filter.title}
                      </FilterToggle>
                    </FilterListItem>
                  );
                })}
              </FilterList>
            )}
          </div>
        );
      })}
    </ExpandingBlock>
  );
};

FilterBlock.defaultProps = {
  collapsible: true,
};

export default FilterBlock;
